/**
 * Sample-data renderer for the Check-In T&C editor's preview.
 *
 * The real resolution happens in `sm4rt-public-api` — it is authoritative
 * about the body it freezes onto a customer's acceptance, so nothing here
 * feeds a customer. This exists so an admin can see what a `{{variable}}`
 * becomes before a customer is the first person to find out.
 *
 * The value map itself comes from the contracts package
 * (`buildCheckInTemplateValues`), which is the same function the public API's
 * Python twin is pinned against by the shared fixture. So the preview is not
 * an approximation of what the customer sees: given the same context, it is
 * the same string.
 *
 * The two sample contexts are chosen to show the honest cases rather than the
 * flattering ones. In-store deliberately has no customer details, because a
 * kiosk customer reads the terms before entering them — that is when
 * `{{customer.name}}` renders "you", and an admin should see it happen here
 * rather than in a photo of a reception iPad.
 */

import type { AppSettings, CheckInTermsIntakeType } from '@techgitter/contracts';
import {
  buildCheckInTemplateValues,
  renderTemplateSource,
  type CheckInTemplateContext,
} from '@techgitter/contracts';

const COMPANY_NAME = 'Tech Solvers';

/** Fixed so the preview does not change under the admin as the clock moves. */
const SAMPLE_TODAY = '2026-05-19';

const SAMPLE_IN_STORE: CheckInTemplateContext = {
  sessionNumber: 'CI-2026-0184',
  intakeType: 'in_store',
  amountDue: 180,
  expiresAt: null,
  termsVersion: '2026-08-15',
  // Unknown at terms time on a kiosk — the fallback case, shown deliberately.
  customerName: null,
  businessName: null,
  customerEmail: null,
  customerPhone: null,
  companyName: COMPANY_NAME,
  today: SAMPLE_TODAY,
  businessHours: null,
  returnGraceDays: 14,
};

const SAMPLE_ONLINE: CheckInTemplateContext = {
  sessionNumber: 'CI-2026-0185',
  intakeType: 'online',
  amountDue: 1234.5,
  expiresAt: '2026-06-19',
  termsVersion: '2026-08-15',
  // An online session is usually raised for a known customer, so the details
  // are already on the row when the link is opened.
  customerName: 'Jane Smith',
  businessName: 'Acme Pty Ltd',
  customerEmail: 'jane@acme.com.au',
  customerPhone: '+61412345678',
  companyName: COMPANY_NAME,
  today: SAMPLE_TODAY,
  businessHours: null,
  returnGraceDays: 14,
};

/**
 * Render a draft body against sample data for one intake type.
 *
 * `appSettings` supplies the real business hours where they are loaded, so the
 * preview quotes this business's actual opening times rather than the defaults.
 */
export function renderCheckInTemplate(
  body: string | null | undefined,
  intakeType: CheckInTermsIntakeType,
  appSettings: AppSettings | null,
): string {
  if (!body) return '';
  const base = intakeType === 'online' ? SAMPLE_ONLINE : SAMPLE_IN_STORE;
  const context: CheckInTemplateContext = {
    ...base,
    businessHours: appSettings?.businessHours ?? null,
    returnGraceDays: appSettings?.returnGraceDays ?? base.returnGraceDays,
  };
  return renderTemplateSource(body, buildCheckInTemplateValues(context));
}
